<template>
    <FileLoading
        v-if="isLoadingAddFileForm"
        class="h-[50vh]"
    />

    <div v-else>
        <GeneralAddFileError />

        <AddFileFormHeader
            :title="formTitle"
            :viewOrderUrl="routes.viewOrderUrl"
        />

        <form
            id="addClientForm"
            data-testid="add-file-form"
            enctype="multipart/form-data"
            onsubmit="return false;"
        >
            <CsrfField />

            <slot name="instructions"></slot>

            <FileDetailsSection />

            <!-- @todo [CLENG-1787] remove key -->
            <ClientsSection
                :key="addFileStore.clientsSectionRemountKey"
                :minVisibleClientInfoForms
            />

            <SendWireInstructionsSection v-if="!addFileStore.tpsOrder?.hideSendWire" />

            <RequestWireInformationSection v-if="!addFileStore.tpsOrder?.hideRecvWire" />

            <RequestIdentityVerificationSection />

            <DocumentUploadSection
                v-if="userStore.showDocuments"
                :routes="routes.documentRoutes"
            >
            </DocumentUploadSection>

            <RequestMortgageInformationSection v-if="featureFlagStore.flags?.enable_seller_auth_task" />

            <slot />

            <NotificationSection class="border-t">
                <template #formAction>
                    <DsButton
                        v-if="!hideSubmitButton"
                        data-testid="add-file-submit"
                        :disabled="addFileStore.isFormDisabled"
                        :loading="isSubmitting"
                        size="md"
                        variant="primary"
                        @click="validateAndConfirm"
                    >
                        Submit file
                    </DsButton>
                </template>
            </NotificationSection>
        </form>
    </div>

    <AddFileConfirmationDialog
        v-model:open="showSummaryDialog"
        :primaryLoading="isSubmitting"
        @confirm="confirmSummary"
    />

    <SimilarEmdDialog
        v-model:open="showSimilarEmdDialog"
        :fullFileFormData="fileFormData"
        :similarEmds
        :submitFormWith
    />

    <EditAvailableClientSheet
        v-model:open="isEditClientSheetOpen"
        :clientId="clientIdBeingEdited"
    />

    <DsToast class="md:top-16" />
</template>

<script setup lang="ts">
import { registerAddFileSubmittedAction } from '@/company-portal/add-file/add-file-reset';
import { useAddFileStore } from '@/company-portal/add-file/add-file.store';
import type { AddFileClient, AddFileRoutes } from '@/company-portal/add-file/add-file.types';
import type { UseAddFileSubmitOptions } from '@/company-portal/add-file/use-add-file-submit';
import { useAddFileSubmit } from '@/company-portal/add-file/use-add-file-submit';
import type { ClientInfoFormOptions } from '@/company-portal/add-file/use-client-info-forms';
import CsrfField from '@/components/auth/CsrfField.vue';
import DsButton from '@/components/design-system/button/DsButton.vue';
import DsToast from '@/components/design-system/toast/DsToast.vue';
import AddFileFormHeader from '@/components/Views/Company/AddFile/AddFileFormHeader.vue';
import ClientsSection from '@/components/Views/Company/AddFile/ClientsSection.vue';
import AddFileConfirmationDialog from '@/components/Views/Company/AddFile/ConfirmationDialog/AddFileConfirmationDialog.vue';
import DocumentUploadSection from '@/components/Views/Company/AddFile/DocumentUploadSection.vue';
import EditAvailableClientSheet from '@/components/Views/Company/AddFile/EditClient/EditAvailableClientSheet.vue';
import FileDetailsSection from '@/components/Views/Company/AddFile/FileDetailsSection.vue';
import GeneralAddFileError from '@/components/Views/Company/AddFile/GeneralAddFileError.vue';
import NotificationSection from '@/components/Views/Company/AddFile/NotificationSection.vue';
import RequestIdentityVerificationSection from '@/components/Views/Company/AddFile/RequestIdentityVerificationSection.vue';
import RequestMortgageInformationSection from '@/components/Views/Company/AddFile/RequestMortgageInformationSection.vue';
import RequestWireInformationSection from '@/components/Views/Company/AddFile/RequestWireInformationSection.vue';
import SendWireInstructionsSection from '@/components/Views/Company/AddFile/SendWireInstructionsSection.vue';
import SimilarEmdDialog from '@/components/Views/Company/AddFile/SimilarEmdDialog.vue';
import FileLoading from '@/components/Views/Company/File/FileLoading.vue';
import { EDIT_AVAILABLE_CLIENT_SHEET_INJECTION_KEY } from '@/injection-keys';
import { useFeatureFlagsStore } from '@/stores/company/feature-flags.store';
import { useUserStore } from '@/stores/company/user.store';
import { computed, onUnmounted, provide, ref, watch } from 'vue';

const props = defineProps<{
    minVisibleClientInfoForms: ClientInfoFormOptions['minVisibleClientInfoForms'];
    routes: AddFileRoutes;
    hideSubmitButton?: boolean;
    formTitle?: string;
}>();

const emit = defineEmits<{
    (e: 'changeSubmitting', isSubmitting: boolean): void;
}>();

const addFileStore = useAddFileStore();
const userStore = useUserStore();
const featureFlagStore = useFeatureFlagsStore();

userStore.getActiveCompanies();
userStore.getActiveBranches();
userStore.getAvailableWireDocs();

const { getTpsOrder, isLoadingTpsOrder } = addFileStore.loadTpsOrder(computed(() => props.routes.loadTpsOrderRoute ?? ''));
getTpsOrder();

const isLoadingAddFileForm = computed(
    () =>
        !featureFlagStore.isFinished ||
        userStore.isFetchingMeDetail ||
        userStore.isFetchingCompanies ||
        userStore.isFetchingBranches ||
        userStore.isFetchingWireDocs ||
        isLoadingTpsOrder.value,
);

/** @todo [CLENG-4993] cleanup when new_add_file_submission_enabled FF is decommissioned */
const submitRoutes = computed<UseAddFileSubmitOptions>(() => {
    /**
     * new file submission
     *
     * the new API routes will carry vendor fields and be
     * used in both TPS & Company Portal contexts
     */
    if (featureFlagStore.flags?.new_add_file_submission_enabled) {
        return {
            validateRoute: props.routes.newFileValidateRoute,
            submitRoute: props.routes.newFileSubmitRoute,
        };
    }

    /**
     * legacy file submission
     *
     * TPS orders submit through the vendor route, whose middleware
     * resolves vendor document temp-paths to files.
     */
    if (addFileStore.tpsOrder) {
        return {
            validateRoute: props.routes.vendorValidateRoute,
            submitRoute: props.routes.vendorSubmitRoute,
        };
    }
    return {
        validateRoute: props.routes.validateRoute,
        submitRoute: props.routes.submitRoute,
    };
});

// submission flow
const {
    isSubmitting,
    similarEmds,
    fileFormData,
    validateAndConfirm,
    confirmSummary,
    submitFormWith,
    showSummaryDialog,
    showSimilarEmdDialog,
} = useAddFileSubmit(submitRoutes);

watch(isSubmitting, newIsSubmitting => {
    emit('changeSubmitting', newIsSubmitting);
});

// edit client sheet
const isEditClientSheetOpen = ref(false);
const clientIdBeingEdited = ref<AddFileClient['id']>();

provide(EDIT_AVAILABLE_CLIENT_SHEET_INJECTION_KEY, {
    editAvailable: true,
    openEditClientSheet: (clientId: AddFileClient['id']) => {
        clientIdBeingEdited.value = clientId;
        isEditClientSheetOpen.value = true;
    },
});

// form reset
const unregisterAddFileSubmittedAction = registerAddFileSubmittedAction(addFileStore.resetAddFileStore);

onUnmounted(unregisterAddFileSubmittedAction);

defineExpose({ validateAndConfirm });
</script>
